import { createFileRoute } from "@tanstack/react-router";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { useCallback, useEffect, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { CrudShellHeader, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";

export const Route = createFileRoute("/app/actividad-cobros")({
  component: Page,
  head: () => ({ meta: [{ title: "Actividad de cobros" }] }),
});

type PaymentEvent = {
  id: string;
  eventType?: string;
  status?: string;
  message?: string;
  amountCents?: number | null;
  createdAt?: string;
  orderId?: string | null;
};

type ProviderRow = {
  id: string;
  label: string;
  tenant?: { priority?: number | null; enabled?: boolean; configured?: boolean } | null;
};

function tone(status?: string) {
  const s = (status ?? "").toLowerCase();
  if (s.includes("paid") || s.includes("approved") || s.includes("confirm")) return "text-emerald-700";
  if (s.includes("fail") || s.includes("reject") || s.includes("error")) return "text-red-700";
  return "text-amber-700";
}

function Page() {
  const tenantId = useWorkspaceTenantId();
  const [events, setEvents] = useState<PaymentEvent[]>([]);
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [dash, prov] = await Promise.all([
        apiGetJson<{ recentEvents?: PaymentEvent[] }>("/api/payments/dashboard", tenantId).catch(
          () => ({ recentEvents: [] as PaymentEvent[] }),
        ),
        apiGetJson<{ providers: ProviderRow[] }>("/api/payments/providers", tenantId),
      ]);
      setEvents(dash.recentEvents ?? []);
      setProviders(prov.providers ?? []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function move(id: string, delta: number) {
    const current = providers.find((p) => p.id === id);
    const priority = (current?.tenant?.priority ?? 100) + delta;
    try {
      await apiSendJson("/api/payments/providers", "POST", { provider: id, priority }, tenantId);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo reordenar");
    }
  }

  return (
    <ClinicalPageShell offset="none" className="pt-6">
      <MerchantExperienceBar screenId="actividad-cobros" />
      <CrudShellHeader
        title="Actividad de cobros"
        description="Feed de webhooks de pago (sm_payment_events) y orden de pasarelas BYO."
      />
      {loading ? (
        <Loading variant="list" rows={4} />
      ) : (
        <div className="grid gap-6 px-4 pb-10 lg:grid-cols-2">
          <section>
            <h3 className="mb-2 text-sm font-semibold">Eventos recientes</h3>
            {events.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin eventos todavía. El feed reusa la tabla existente de FM.</p>
            ) : (
              <ul className="divide-y rounded-xl border">
                {events.map((e) => (
                  <li key={e.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                    <div>
                      <p className="font-medium">{e.eventType ?? "evento"}</p>
                      <p className="text-xs text-muted-foreground">{e.message ?? e.orderId ?? e.id}</p>
                    </div>
                    <span className={`text-xs font-medium ${tone(e.status)}`}>{e.status ?? "pendiente"}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section>
            <h3 className="mb-2 text-sm font-semibold">Orden de pasarelas</h3>
            <p className="mb-2 text-xs text-muted-foreground">
              Persistido en la config de proveedores del tenant (campo priority). El checkout público ya usa ese orden.
            </p>
            <ul className="divide-y rounded-xl border">
              {providers.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                  <span>
                    {p.label}
                    {p.tenant?.configured ? " · configurada" : " · sin credenciales"}
                  </span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => void move(p.id, -10)}>
                      Subir
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => void move(p.id, 10)}>
                      Bajar
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </ClinicalPageShell>
  );
}
